<template>
  <div class="max-w-7xl mx-auto pt-4 pb-24">
    <div class="flex flex-col lg:flex-row gap-6">
      <!-- 左侧主内容区 -->
      <div class="w-full lg:w-[70%] space-y-6">
        <!-- 生成中动画 -->
        <transition name="fade-slide">
          <div
            v-if="showGeneratingAnimation"
            class="bg-gradient-to-r from-[#F2C94C]/10 to-[#F2C94C]/5 border border-[#F2C94C]/30 p-8 rounded-3xl"
          >
            <div class="flex items-center gap-4">
              <div class="relative">
                <div class="w-12 h-12 rounded-2xl bg-[#F2C94C]/20 flex items-center justify-center">
                  <IconLoader class="w-6 h-6 text-[#F2C94C] animate-spin" />
                </div>
                <div class="absolute -top-1 -right-1 w-4 h-4 bg-[#F2C94C] rounded-full animate-pulse" />
              </div>
              <div>
                <h3 class="text-lg font-black text-slate-900">AI 正在深度分析</h3>
                <p class="text-sm text-slate-500">基于您的诊断结果，生成符合实际现状的实战建议...</p>
              </div>
            </div>
            <div class="mt-6 flex gap-2">
              <div
                v-for="i in 3"
                :key="i"
                class="h-1 bg-[#F2C94C] rounded-full flex-1 generating-bar"
                :style="{ animationDelay: (i - 1) * 0.2 + 's' }"
              />
            </div>
          </div>
        </transition>

        <!-- 诊断结果内容 -->
        <template v-if="!showGeneratingAnimation && prescription">
          <!-- 真相揭秘 -->
          <div class="bg-white border border-slate-200 p-6 md:p-8 rounded-3xl shadow-sm animate-fade-in">
            <div class="flex items-center gap-3 mb-4">
              <div class="w-10 h-10 rounded-xl bg-[#F2C94C]/10 flex items-center justify-center text-[#F2C94C]">
                <IconSparkles class="w-5 h-5" />
              </div>
              <div>
                <h3 class="text-lg font-black text-slate-900">本质原因</h3>
                <p class="text-xs text-slate-400">AI 深度诊断分析</p>
              </div>
            </div>
            <div class="text-slate-800 text-[15px] leading-relaxed">
              {{ truthText }}
              <span
                v-if="showTruth && truthText.length < (prescription.truth?.length || 0)"
                class="inline-block w-0.5 h-5 bg-[#F2C94C] animate-pulse ml-1"
              />
            </div>
          </div>

          <!-- 谈话剧本 -->
          <transition name="fade-slide-up">
            <div
              v-if="showScript"
              class="bg-white border border-slate-200 p-6 md:p-8 rounded-3xl shadow-sm"
            >
              <div class="flex items-center gap-3 mb-6">
                <div class="w-10 h-10 rounded-xl bg-[#1B3C59]/10 flex items-center justify-center text-[#1B3C59]">
                  <IconMessageSquare class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-slate-900">谈话剧本</h3>
                  <p class="text-xs text-slate-400">实战级沟通话术参考</p>
                </div>
              </div>

              <div class="space-y-6">
                <!-- 开场白 -->
                <div>
                  <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">开场白</span>
                  <div class="mt-2 p-4 bg-slate-50 rounded-2xl border-l-4 border-[#1B3C59]">
                    <p class="text-slate-700 italic">"{{ openingText }}"</p>
                  </div>
                </div>

                <!-- 核心拆解 -->
                <div
                  v-for="(response, idx) in prescription.script?.responses"
                  :key="idx"
                  class="flex gap-4 animate-slide-in-left"
                  :style="{ animationDelay: idx * 0.2 + 's' }"
                >
                  <div class="w-8 h-8 rounded-full bg-[#1B3C59] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {{ idx + 1 }}
                  </div>
                  <p class="text-slate-700 pt-1">{{ response }}</p>
                </div>

                <!-- 收尾 -->
                <div v-if="prescription.script?.closing">
                  <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">收尾动作</span>
                  <div class="mt-2 p-4 bg-slate-50 rounded-2xl border-l-4 border-[#1B3C59]">
                    <p class="text-slate-700 italic">"{{ prescription.script.closing }}"</p>
                  </div>
                </div>
              </div>
            </div>
          </transition>

          <!-- 动作红线 -->
          <transition name="fade-slide-up">
            <div
              v-if="showRedLines && prescription.redLines && prescription.redLines.length > 0"
              class="bg-red-50 border border-red-200 p-6 md:p-8 rounded-3xl"
            >
              <div class="flex items-center gap-3 mb-4">
                <div class="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-red-600">
                  <IconOctagonAlert class="w-5 h-5" />
                </div>
                <div>
                  <h3 class="text-lg font-black text-red-900">动作红线</h3>
                  <p class="text-xs text-red-400">绝对避免的管理误区</p>
                </div>
              </div>
              <ul class="space-y-3">
                <li
                  v-for="(line, idx) in prescription.redLines"
                  :key="idx"
                  class="flex items-start gap-3 animate-slide-in-left"
                  :style="{ animationDelay: idx * 0.1 + 's' }"
                >
                  <IconOctagonAlert class="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <span class="text-red-800">{{ line }}</span>
                </li>
              </ul>
            </div>
          </transition>
        </template>

        <!-- 追问对话区 -->
        <transition name="fade-slide-up">
          <div v-if="showRedLines" class="space-y-4">
            <div class="flex items-center gap-2">
              <IconMessageSquare class="w-4 h-4 text-[#F2C94C]" />
              <span class="text-xs font-bold text-slate-500 uppercase tracking-wider">持续追问</span>
            </div>

            <!-- 对话历史 -->
            <div v-if="localChatHistory.length > 0" class="space-y-4 max-h-[400px] overflow-y-auto pr-2">
              <div
                v-for="(message, index) in localChatHistory"
                :key="message.id"
                class="animate-fade-in"
                :class="message.role === 'user' ? 'flex justify-end' : 'flex justify-start'"
                :style="{ animationDelay: index * 0.05 + 's' }"
              >
                <div
                  class="max-w-[85%] rounded-2xl px-5 py-4"
                  :class="message.role === 'user' ? 'bg-[#1B3C59] text-white' : 'bg-white border border-slate-200 text-slate-800'"
                >
                  <div v-if="message.role === 'ai'" class="flex items-center gap-2 mb-2">
                    <div class="w-5 h-5 rounded-full bg-[#F2C94C]/10 flex items-center justify-center">
                      <IconSparkles class="w-3 h-3 text-[#F2C94C]" />
                    </div>
                    <span class="text-xs font-bold text-[#F2C94C]">AI 管理能力提升助手</span>
                  </div>
                  <div class="text-sm leading-relaxed">{{ message.content }}</div>
                </div>
              </div>
              <div v-if="isChatLoading" class="flex justify-start">
                <div class="bg-white border border-slate-200 rounded-2xl px-5 py-4">
                  <div class="flex items-center gap-2">
                    <IconLoader class="w-4 h-4 text-[#F2C94C] animate-spin" />
                    <span class="text-sm text-slate-400">思考中...</span>
                  </div>
                </div>
              </div>
              <div ref="chatEndRef" />
            </div>

            <!-- 追问输入框 -->
            <div class="bg-white border border-slate-200 p-4 rounded-2xl shadow-sm">
              <IntentionCapture
                @search="handleFollowUp"
                mode="follow-up"
                placeholder="基于以上研判，您还有哪些具体的实战困惑？"
              />
            </div>
          </div>
        </transition>
      </div>

      <!-- 右侧边栏 - 仅展示专家 -->
      <div class="w-full lg:w-[30%] space-y-6">
        <div v-if="expertResources.length > 0" class="space-y-4">
          <div class="flex items-center gap-3">
            <div class="w-1 h-3 bg-[#F2C94C]/50 rounded-full"></div>
            <h3 class="text-[9px] font-black text-slate-400 tracking-widest uppercase">推荐专家</h3>
          </div>
          <div class="space-y-3">
            <div
              v-for="(expert, idx) in expertResources.slice(0, 2)"
              :key="expert.id"
              class="bg-white border border-slate-100 p-4 rounded-2xl shadow-sm hover:shadow-md transition-all group hover:border-[#F2C94C]/30"
            >
              <div class="flex items-center gap-3">
                <img
                  :src="expert.avatar"
                  class="w-12 h-12 rounded-full object-cover border-2 border-transparent group-hover:border-[#F2C94C] transition-all cursor-pointer"
                  :alt="expert.name"
                  referrerPolicy="no-referrer"
                  @click="navigateToExpert(expert.id)"
                />
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2">
                    <div class="text-sm font-black text-slate-900 truncate">{{ expert.name }}</div>
                    <span
                      v-if="getMatchInfo(expert) && idx === 0"
                      class="px-2 py-0.5 bg-[#F2C94C]/10 text-[#F2C94C] text-[9px] font-bold rounded-full flex items-center gap-1"
                    >
                      <IconAward class="w-3 h-3" />
                      推荐
                    </span>
                  </div>
                  <div class="text-[10px] text-slate-400 font-bold truncate">
                    {{ expert.department ? `${expert.department} · ${expert.position || expert.title}` : expert.title }}
                  </div>
                  <div v-if="getMatchInfo(expert)" class="flex items-center gap-2 mt-1">
                    <div class="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        class="h-full bg-gradient-to-r from-[#F2C94C] to-[#F2C94C]/70 rounded-full"
                        :style="{ width: getMatchInfo(expert).score + '%' }"
                      />
                    </div>
                    <span class="text-[9px] text-slate-400">{{ getMatchInfo(expert).score }}分</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { generateManagementFeedback, calculateExpertMatches } from '../../services/ai-service'
import IntentionCapture from '../practice/IntentionCapture.vue'
import {
  IconSparkles,
  IconMessageSquare,
  IconOctagonAlert,
  IconAward,
  IconLoader
} from '../icons'

export default {
  name: 'DiagnosticResultView',
  components: {
    IntentionCapture,
    IconSparkles,
    IconMessageSquare,
    IconOctagonAlert,
    IconAward,
    IconLoader
  },
  props: {
    topic: {
      type: Object,
      required: true
    },
    prescription: {
      type: Object,
      default: null
    },
    experts: {
      type: Array,
      default: () => []
    },
    context: {
      type: Object,
      required: true
    },
    diagnosticContext: {
      type: Object,
      default: null
    },
    isGenerating: {
      type: Boolean,
      default: false
    },
    chatHistory: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localChatHistory: this.chatHistory,
      isChatLoading: false,
      showGeneratingAnimation: this.isGenerating,
      showTruth: false,
      showScript: false,
      showRedLines: false,
      truthText: '',
      openingText: '',
      truthTimer: null,
      scriptTimer: null,
      typewriterTimer: null,
      typewriterOpeningTimer: null
    }
  },
  computed: {
    expertMatches() {
      const matches = calculateExpertMatches(
        this.topic.title,
        this.context,
        this.experts.map(e => ({ id: e.id, topics: e.topics, tags: e.tags, resume: e.resume }))
      )
      return matches.slice(0, 3)
    },
    matchedExperts() {
      return this.expertMatches
        .map(m => this.experts.find(e => e.id === m.expertId))
        .filter(Boolean)
    },
    expertResources() {
      return this.matchedExperts.length > 0 ? this.matchedExperts : this.experts.slice(0, 3)
    }
  },
  watch: {
    chatHistory: {
      handler(newVal) {
        this.localChatHistory = newVal
      },
      deep: true
    },
    localChatHistory() {
      this.$nextTick(() => {
        this.scrollToBottom()
      })
    },
    isGenerating: {
      handler(newVal) {
        if (newVal) {
          this.showGeneratingAnimation = true
          this.showTruth = false
          this.showScript = false
          this.showRedLines = false
          this.truthText = ''
          this.openingText = ''
        }
      },
      immediate: true
    },
    prescription: {
      handler(newVal) {
        if (newVal && !this.isGenerating) {
          this.startAnimationSequence()
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this.clearTimers()
  },
  methods: {
    clearTimers() {
      if (this.truthTimer) clearTimeout(this.truthTimer)
      if (this.scriptTimer) clearTimeout(this.scriptTimer)
      if (this.typewriterTimer) clearInterval(this.typewriterTimer)
      if (this.typewriterOpeningTimer) clearInterval(this.typewriterOpeningTimer)
    },
    startAnimationSequence() {
      this.showGeneratingAnimation = false
      this.showTruth = true

      // 打字机效果 - truth
      this.runTypewriter(this.prescription.truth || '', 25, (text) => {
        this.truthText = text
      })

      // 依次显示各部分内容
      this.truthTimer = setTimeout(() => {
        this.showScript = true
        // 开场白打字机
        this.$nextTick(() => {
          this.runTypewriter(this.prescription.script?.opening || '', 20, (text) => {
            this.openingText = text
          })
        })
      }, (this.prescription.truth?.length || 0) * 25 + 500)

      this.scriptTimer = setTimeout(() => {
        this.showRedLines = true
      }, (this.prescription.truth?.length || 0) * 25 + (this.prescription.script?.opening?.length || 0) * 20 + 1000)
    },
    runTypewriter(text, speed, callback) {
      if (!text) {
        callback('')
        return
      }
      let i = 0
      callback('')
      const timer = setInterval(() => {
        if (i < text.length) {
          callback(text.slice(0, i + 1))
          i++
        } else {
          clearInterval(timer)
        }
      }, speed)
      // 保存引用以便清理
      if (speed === 25) {
        this.typewriterTimer = timer
      } else {
        this.typewriterOpeningTimer = timer
      }
    },
    scrollToBottom() {
      if (this.$refs.chatEndRef) {
        this.$refs.chatEndRef.scrollIntoView({ behavior: 'smooth' })
      }
    },
    getMatchInfo(expert) {
      return this.expertMatches.find(m => m.expertId === expert.id)
    },
    navigateToExpert(expertId) {
      this.$router.push(`/expert/${expertId}`)
    },
    async handleFollowUp(query) {
      if (!query.trim()) return

      const userMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: query
      }

      const updatedHistory = [...this.localChatHistory, userMessage]
      this.localChatHistory = updatedHistory
      this.$emit('update-history', updatedHistory)

      this.isChatLoading = true

      try {
        const response = await generateManagementFeedback(
          query,
          this.context,
          this.diagnosticContext || null,
          updatedHistory.map(m => ({ role: m.role, content: m.content }))
        )

        const aiMessage = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          content: response
        }

        const finalHistory = [...updatedHistory, aiMessage]
        this.localChatHistory = finalHistory
        this.$emit('update-history', finalHistory)
      } catch (error) {
        const errorMessage = {
          id: (Date.now() + 1).toString(),
          role: 'ai',
          content: '抱歉，AI 管理能力提升助手的通讯线路受到干扰，请稍后再试。'
        }
        const finalHistory = [...updatedHistory, errorMessage]
        this.localChatHistory = finalHistory
        this.$emit('update-history', finalHistory)
      } finally {
        this.isChatLoading = false
      }
    }
  }
}
</script>

<style scoped>
/* 生成中进度条动画 */
@keyframes generating-bar {
  0% { width: 0%; }
  50% { width: 100%; }
  100% { width: 0%; }
}

.generating-bar {
  animation: generating-bar 1.5s ease-in-out infinite;
}

/* fade-slide 进入/离开动画 */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* fade-slide-up 进入/离开动画 */
.fade-slide-up-enter-active,
.fade-slide-up-leave-active {
  transition: all 0.4s ease;
}

.fade-slide-up-enter,
.fade-slide-up-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* 淡入动画 */
.animate-fade-in {
  animation: fadeIn 0.5s ease-out forwards;
}

@keyframes fadeIn {
  0% { opacity: 0; }
  100% { opacity: 1; }
}

/* 左侧滑入动画 */
.animate-slide-in-left {
  animation: slideInLeft 0.3s ease-out forwards;
  opacity: 0;
}

@keyframes slideInLeft {
  0% { opacity: 0; transform: translateX(-20px); }
  100% { opacity: 1; transform: translateX(0); }
}
</style>
